// Simple per-browser autocomplete of email recipients used across the app
// (Downloads bulk email + Client bulk email). Persisted in localStorage,
// no backend required.
//
// Kept as a plain module (not a hook) so any component can read/write.
// Consumers should call `useEmailHistory()` to get a reactive list.

import { useEffect, useState } from "react";

const KEY = "cosm_email_history";
const MAX = 40;

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(x => typeof x === "string" && x.includes("@")) : [];
  } catch { return []; }
}

function save(list) {
  try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch { /* ignore */ }
}

// Cross-component notifier so both Downloads and ClientEdit stay in sync
// when either one adds or removes an entry (they are unlikely open at the
// same time, but this keeps the state model tidy).
const listeners = new Set();
function notify() { listeners.forEach(fn => fn()); }

export function addEmails(newList) {
  const clean = newList
    .map(s => (s || "").trim().toLowerCase())
    .filter(s => s.includes("@") && s.length < 200);
  if (!clean.length) return;
  const cur = load();
  // Move existing to front, prepend new ones
  const merged = [...clean, ...cur.filter(e => !clean.includes(e))].slice(0, MAX);
  save(merged);
  notify();
}

export function removeEmail(email) {
  const cur = load().filter(e => e !== email);
  save(cur);
  notify();
}

export function useEmailHistory() {
  const [list, setList] = useState(load);
  useEffect(() => {
    const fn = () => setList(load());
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return list;
}
